import React, { useContext, useState } from "react";

import {
  Alert,
  DialogContentText,
  Link,
  ListItem,
  Snackbar,
} from "@mui/material";

import { DEFAULT_SNACKBAR_DURATION } from "../../constants";
import ConfirmDialog from "../shared/confirm-dialog";
import { spotFixTemplateSiteIssue } from "api/endpoints/templates-inspections";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";

interface SpotFixTemplateDialogProps {
  docSearchQueryState: any;
  regionCode: string;
  open: boolean;
  setOpen: (newOpen: boolean) => void;
  handleDocumentSearch: () => void;
}

const SpotFixTemplateDialog = ({
  docSearchQueryState,
  regionCode,
  open,
  setOpen,
  handleDocumentSearch,
}: SpotFixTemplateDialogProps) => {
  const [
    spotFixTemplateSiteIssueSnackbar,
    setSpotFixTemplateSiteIssueSnackbar,
  ] = useState(false);

  const { showError } = useContext(SnackbarContext);

  const handleSpotFixTemplate = (regionCode: string) => {
    spotFixTemplateSiteIssue(
      {
        templateId: docSearchQueryState.docId,
      },
      callOptionsHeaders(regionCode, docSearchQueryState.docId),
    )
      .then(() => {
        setSpotFixTemplateSiteIssueSnackbar(true);
        handleDocumentSearch();
      })
      .catch(showError);
  };

  return (
    <>
      <ConfirmDialog
        title="Spot Fix Template Site Issue"
        open={open}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Fix Template"
        setOpen={setOpen}
        onConfirm={() => handleSpotFixTemplate(regionCode)}
      >
        <DialogContentText align="left" alignItems="center">
          <p>
            Sites functionality in inspections was introduced in 3 stages:
            <ListItem>Stage 1 - where the site was mandatory (OIE)</ListItem>
            <ListItem>Stage 2 - where the user could modify the site</ListItem>
            <ListItem>
              Stage 3 - where the customer has the power to choose if the site
              question is there
            </ListItem>
            <br />
            We are currently in Stage 3 for all new templates and inspections.
            Due to this staged process, the implementation is not how we expect
            and there are legacy code paths and documents (templates and
            inspections) that have problems. The above stages and steps led to
            the following issues:
            <ListItem>
              Missing site - This occurs when a user cannot see a site question
              in their template (i.e. in the template editor), but for some
              reason can see site questions in their inspections.{" "}
            </ListItem>
            <ListItem>
              Double site - This occurs when a user can see a site question in
              their template, but when they start an inspection, it still has
              that automatic site question. As a result, they end up with two
              site questions.{" "}
            </ListItem>
            <br />
            This spot fix checks the configuration of the template and updates
            it to the correct configuration that is needed for the template to
            be in stage 3. For more engineering details about the problem, read
            <Link href="https://safetyculture.atlassian.net/wiki/spaces/SIKB/pages/3137373438/Periscope+How+to+spot+fix+template+site+issues#Why-spot-fix-template-site-issues-using-Periscope">
              {" "}
              here
            </Link>
            <br /> <br /> PLEASE MAKE SURE THE USER DOES NOT HAVE ANY
            UNPUBLISHED CHANGES!!
            <br />
            Spot fixing with unpublished changes can cause this issue to arise
            again.
          </p>
        </DialogContentText>
      </ConfirmDialog>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={spotFixTemplateSiteIssueSnackbar}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSpotFixTemplateSiteIssueSnackbar(false)}
        data-testid="spot-fix-template-site-issue-success"
      >
        <Alert severity="success" variant="filled">
          Successfully spot fixed site issue in template
        </Alert>
      </Snackbar>
    </>
  );
};
export default SpotFixTemplateDialog;
